import { Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  first,
  map,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import {
  CurrentUserEntityActions,
  UserActions,
  UserSelectors,
  AccountSelectors,
} from '@recruitee/account-data';
import { AsyncJobService, AsyncJobStatus } from '@recruitee/async-job-data';
import { AsyncJobType } from '@recruitee/async-job-types';
import { filterBoolean } from '@recruitee/common';
import type { SampleDataRemoveContext } from '@recruitee/company-logic';
import { SampleDataAnalyticsService } from '@recruitee/company-logic';
import type { Company } from '@recruitee/company-types';
import { I18nService } from '@recruitee/i18n';
import { AppStore } from '@recruitee/legacy-app-store';
import { JobItemsActions, OfferItemsActions } from '@recruitee/offers-data';
import { SampleDataActions } from '@recruitee/sample-data-data';
import { DialogService, DialogWidth } from '@recruitee/user-interface';

import {
  ActionType,
  fetchRecords as searchFetchRecords,
} from '../../search/search-page/search-page.actions';
import { NoAccessDialogComponent } from './no-access-dialog/no-access-dialog.component';

@Injectable({ providedIn: 'root' })
export class SampleDataService {
  private readonly currentCompany$ = this.store
    .select(AccountSelectors.currentCompany)
    .pipe(filterBoolean);

  private readonly companyAdmins$ = this.currentCompany$.pipe(
    tap(() => this.store.dispatch(UserActions.fetchCollection())),
    switchMap(currentCompany => this.store.select(UserSelectors.entities())),
    filterBoolean,
    map(admins => admins.filter(admin => admin.roleAdmin)),
  );

  private readonly companyLoaded$ = this.store.select(AccountSelectors.currentCompany).pipe(
    filterBoolean,
    map(company => !!company),
    take(1),
  );

  public readonly hasSampleData$: Observable<boolean> = this.store
    .select(AccountSelectors.currentCompany)
    .pipe(map(({ hasSampleData }: Company) => hasSampleData));

  public readonly sampleDataInProgress$: Observable<boolean> = combineLatest([
    this.asyncJobsService.getJobs$(AsyncJobType.RemoveSampleData),
    this.asyncJobsService.getJobs$(AsyncJobType.GenerateSampleData),
  ]).pipe(
    map(([removeSampleDataJobs, generateSampleDataJobs]) =>
      [...removeSampleDataJobs, ...generateSampleDataJobs].some(
        job => job.status !== AsyncJobStatus.FINISHED,
      ),
    ),
    switchMap(inProgress =>
      inProgress
        ? of(inProgress)
        : of(inProgress).pipe(
            switchMap(value =>
              combineLatest([
                this.actions$.pipe(ofType(ActionType.FETCH_RECORDS_SUCCESS)).pipe(take(1)),
                this.actions$.pipe(ofType(JobItemsActions.fetchCollectionSuccess)).pipe(take(1)),
                this.actions$.pipe(ofType(CurrentUserEntityActions.fetchSuccess)).pipe(take(1)),
              ]).pipe(map(() => value)),
            ),
          ),
    ),
    startWith(false),
  );

  public readonly sampleDataFinished$ = this.sampleDataInProgress$.pipe(
    filterBoolean,
    switchMap(() =>
      combineLatest([
        this.asyncJobsService.getJobs$(AsyncJobType.RemoveSampleData),
        this.asyncJobsService.getJobs$(AsyncJobType.GenerateSampleData),
      ]).pipe(
        map(jobs => {
          const sampleDataJobs = jobs.flat();
          const jobsNotFinished = sampleDataJobs.some(
            job => job.status !== AsyncJobStatus.FINISHED,
          );
          return !jobsNotFinished;
        }),
        distinctUntilChanged(),
        filterBoolean,
        debounceTime(0),
      ),
    ),
  );

  //  eslint-disable-next-line @typescript-eslint/member-ordering
  public readonly appDataRefresh$ = combineLatest([this.sampleDataFinished$, this.companyLoaded$]);
  private currentCompany: Company;

  constructor(
    private dialogService: DialogService,
    private i18nService: I18nService,
    private asyncJobsService: AsyncJobService,
    private appStore: AppStore,
    private store: Store,
    private sampleDataAnalyticsService: SampleDataAnalyticsService,
    private actions$: Actions,
  ) {
    this.appDataRefresh$.subscribe(() => {
      this.store.dispatch(searchFetchRecords());
      this.store.dispatch(OfferItemsActions.fetchActiveCollections());
      this.store.dispatch(CurrentUserEntityActions.fetch());
    });

    this.currentCompany$.subscribe(currentCompany => (this.currentCompany = currentCompany));
  }

  public onRemoveSampleData(origin: SampleDataRemoveContext): void {
    const canRemoveData = this.currentCompany?.isAdmin;
    if (canRemoveData) {
      this.dialogService
        .information({
          title: this.i18nService.translate('app.onboarding.trial_center.confirm_remove.title'),
          message: this.i18nService.translate('app.onboarding.trial_center.confirm_remove.message'),
          agreeLabel: this.i18nService.translate(
            'app.onboarding.trial_center.confirm_remove.agree_label',
          ),
          width: DialogWidth.SmallXX,
        })
        .pipe(first(), filterBoolean)
        .subscribe(_ => {
          this.sampleDataAnalyticsService.trackRemoveSampleData(origin);
          this.store.dispatch(SampleDataActions.deleteSampleData());
        });
    } else {
      this.companyAdmins$.pipe(first()).subscribe(admins => {
        this.dialogService.openCustom(
          NoAccessDialogComponent,
          { width: DialogWidth.Small },
          {
            title: this.i18nService.translate('app.onboarding.no_access_dialog.remove_data.title'),
            description: this.i18nService.translate(
              'app.onboarding.no_access_dialog.remove_data.description',
            ),
            imageSrc: 'assets/images/onboarding/removing-sample-data.svg',
            admins: admins.slice(0, 3),
          },
        );
      });
    }
  }

  public onAddSampleData(): void {
    const canAddData = this.currentCompany?.isAdmin;
    if (canAddData) {
      this.sampleDataAnalyticsService.trackFillAccountWithSampleData();
      this.store.dispatch(SampleDataActions.createSampleData());
    } else {
      this.companyAdmins$.pipe(first()).subscribe(admins => {
        this.dialogService.openCustom(
          NoAccessDialogComponent,
          { width: DialogWidth.Small },
          {
            title: this.i18nService.translate('app.onboarding.no_access_dialog.add_data.title'),
            description: this.i18nService.translate(
              'app.onboarding.no_access_dialog.add_data.description',
            ),
            imageSrc: 'assets/images/onboarding/adding-sample-data.svg',
            admins: admins.slice(0, 3),
          },
        );
      });
    }
  }
}
